import { useTranslation } from 'react-i18next';
import { useFieldArray, useWatch, type UseFormReturn } from 'react-hook-form';
import { Button } from '@/shared/ui/buttons/Button';
import { PlusIcon, TriangleAlertIcon } from '@/shared/ui/icons';
import { FeaturesSplitsTargetFields } from '@/features/splits/modules/ui/SplitTargetFields';
import {
  buildEmptyTarget,
  findDistributionMismatches,
  type SplitTargetsFormValues,
} from '@/features/splits/modules/types';
import type { ApiParcelsProductsGetItem } from '@/shared/types/api/parcels-products';

type Props = {
  form: UseFormReturn<SplitTargetsFormValues>;
  onSubmit: (values: SplitTargetsFormValues) => void;
  id?: string;
  submitting?: boolean;
  // Parcel products: drive the allocation editor + exact-distribution guard.
  products: ApiParcelsProductsGetItem[];
  // Source parcel id, forwarded to each target row so the limit baseline
  // excludes this parcel's own products.
  parcelId: string;
};

export const FeaturesSplitsTargetsForm = ({
  form,
  onSubmit,
  id,
  submitting,
  products,
  parcelId,
}: Props) => {
  const { t } = useTranslation('splits');
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'targets' });

  // Live exact-distribution check: every product must be allocated EXACTLY
  // across all targets. Recomputed on every allocation edit so the warning and
  // the disabled save button stay in sync. The server enforces the same rule.
  const watchedTargets = useWatch({ control: form.control, name: 'targets' });
  const mismatches = findDistributionMismatches(
    (watchedTargets ?? []) as SplitTargetsFormValues['targets'],
    products,
  );

  return (
    <form id={id} onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      {fields.map((field, index) => (
        <FeaturesSplitsTargetFields
          key={field.id}
          form={form}
          index={index}
          onRemove={() => remove(index)}
          canRemove={fields.length > 1}
          products={products}
          parcelId={parcelId}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        block
        leftIcon={<PlusIcon className="h-4 w-4" />}
        onClick={() => append(buildEmptyTarget(products), { shouldFocus: false })}
      >
        {t('addTarget')}
      </Button>

      {mismatches.length > 0 && (
        <div className="flex flex-col gap-1 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
          <div className="flex items-center gap-2 font-medium">
            <TriangleAlertIcon className="h-4 w-4" />
            {t('distribution.error')}
          </div>
          <ul className="list-disc pl-5">
            {mismatches.map((m) => (
              <li key={m.product_id}>
                {t('distribution.mismatchLine', {
                  name: m.full_name,
                  allocated: m.allocated,
                  expected: m.expected,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Button type="submit" loading={submitting} disabled={mismatches.length > 0}>
        {t('saveTargets')}
      </Button>
    </form>
  );
};
